import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { ECategory, ENotificationType } from '@/enums';
import { Loader2 } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SearchFormValues } from '../schema';

const categories = [
  {
    label: 'Local',
    value: ECategory.LOCAL
  },
  {
    label: 'Diaspora',
    value: ECategory.DIASPORA
  }
];

const allOptions = [
  {
    label: 'TextSMS',
    value: ENotificationType.SMS
  },
  {
    label: 'TextEmailAddress',
    value: ENotificationType.EMAIL
  }
];

const singleOption = [
  {
    label: 'TextSMS',
    value: ENotificationType.SMS
  }
];

interface SearchFormProps {
  isLoading: boolean;
  submitted?: boolean;
  onSearch: (data: SearchFormValues) => void;
  onCancel: () => void;
}

export function SearchForm({ isLoading, submitted, onSearch, onCancel }: SearchFormProps) {
  const { t } = useTranslation();
  const form = useFormContext<SearchFormValues>();

  const onSubmit = (data: SearchFormValues) => {
    onSearch(data);
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-semibold text-[#34435d]">
                {t('TextRegistrationType')}
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('PSelectCategory')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {categories.map((category, i) => (
                    <SelectItem key={i} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="nationalId"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-semibold text-[#34435d]">
                {t('TextNationalIdNumber')}
              </FormLabel>
              <FormControl>
                <Input disabled={isLoading} placeholder={t('PNationalID')} {...field} />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        {form.watch('category') === ECategory.LOCAL && (
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-[#34435d]">
                  {t('TextPhoneNumber')}
                </FormLabel>
                <FormControl>
                  <Input disabled={isLoading} placeholder={t('PPhoneNumber')} {...field} />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
        )}

        {form.watch('category') === ECategory.DIASPORA && (
          <FormField
            control={form.control}
            name="motherName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-[#34435d]">
                  {t('TextMotherName')}
                </FormLabel>
                <FormControl>
                  <Input disabled={isLoading} placeholder={t('PMotherNames')} {...field} />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem className="relative mt-1 rounded-xl border border-[#dce5f1] bg-white px-4 pb-4 pt-5">
              <FormLabel className="absolute -top-2.5 left-4 bg-white px-1.5 text-sm font-semibold text-[#34435d]">
                {t('TextSendOTP')}:
              </FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex flex-row flex-wrap gap-x-6 gap-y-3"
                >
                  {form.watch('category') === ECategory.LOCAL &&
                    singleOption.map((opt, i) => (
                      <FormItem className="flex items-center space-x-2.5 space-y-0" key={i}>
                        <FormControl>
                          <RadioGroupItem
                            value={opt.value}
                            className="border-[#93a0b4] text-[#2374cf] data-[state=checked]:border-[#2374cf]"
                          />
                        </FormControl>
                        <FormLabel className="font-normal text-[#465572]">{t(opt.label)}</FormLabel>
                      </FormItem>
                    ))}

                  {form.watch('category') === ECategory.DIASPORA &&
                    allOptions.map((opt, i) => (
                      <FormItem className="flex items-center space-x-2.5 space-y-0" key={i}>
                        <FormControl>
                          <RadioGroupItem value={opt.value} />
                        </FormControl>
                        <FormLabel className="font-normal text-[#465572]">{t(opt.label)}</FormLabel>
                      </FormItem>
                    ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {form.watch('type') === ENotificationType.EMAIL && (
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="sr-only">{t('TextEmailAddress')}</FormLabel>
                <FormControl>
                  <Input
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect="off"
                    disabled={isLoading}
                    placeholder={t('PEmailAddress')}
                    {...field}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
        )}

        {form.watch('type') === ENotificationType.SMS &&
          form.watch('category') === ECategory.DIASPORA && (
            <FormField
              control={form.control}
              name="phoneIntl"
              render={({ field }) => (
                <FormItem className="flex flex-col items-start">
                  <FormControl className="w-full">
                    <PhoneInput placeholder={t('PPhoneNumber')} {...field} />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          )}
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        {!submitted && (
          <Button className="bg-[#338AF3] hover:bg-blue-800" type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('TextSearching')}
              </>
            ) : (
              t('TextSearch')
            )}
          </Button>
        )}
        <Button
          className="text-black bg-gray-100 hover:bg-gray-200"
          type="button"
          onClick={onCancel}
        >
          {t('TextCancel')}
        </Button>
      </div>
    </form>
  );
}
